import Image from "next/image";
import { useRouter } from "next/router";

import { getBlogData, getBlogListData } from "@/api/server/blog";
import Banner from "@/components/data-display/banner";
import { BlogList } from "@/components/sections";
import SocialShare from "@/components/sections/socialShare";
import MetaData from "@/components/seo/MetaData";
import { revalidationTime } from "@/utils/constants";
import statusCodes from "@/utils/constants/http-status-codes";

function Blog({ data }) {
  const router = useRouter();

  const MAX_BLOG_TO_SHOW = 3;
  const BLOG_URL = `${process.env.NEXT_PUBLIC_SITE_ABSOLUTE_URL}${router.asPath}`;

  return (
    <>
      <MetaData {...data?.metadata} schema={data?.schema} type="article" />

      <div className="container pb-[6rem] pt-[3rem]">
        <div>
          <Banner
            imageUrl={data?.article?.banner}
            className="aspect-[16/7] lg:aspect-[4/1]"
            imageOptions={{ style: "rounded-xl" }}
          />
        </div>

        <div className="mt-8 lg:mt-[3.75rem]">
          <div className="grid w-full grid-cols-12">
            <div className="col-span-3 hidden lg:block"></div>
            <div className="col-span-12 lg:col-span-6">
              <h1 className="text-2xl font-bold">{data?.article?.title}</h1>
            </div>
            <div className="col-span-3 hidden lg:block"></div>
          </div>

          <div className="mt-9 grid w-full grid-cols-12">
            <div className="col-span-3 hidden lg:block">
              <SocialShare
                shareAbleLink={BLOG_URL}
                title={data?.article?.title}
              />
            </div>
            <div className="col-span-12 lg:col-span-6">
              <p className="lg:text-heading4  text-grayD3 text-base font-medium">
                {data?.article?.short_description}
              </p>

              <div className="mt-11 flex items-center justify-between">
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <div className="relative mr-[0.85rem] h-[2.5rem] w-[2.5rem]">
                    <Image
                      fill
                      sizes="50px"
                      className="rounded-full object-cover"
                      alt={data?.article?.authors?.[0]?.name}
                      src={data?.article?.authors?.[0]?.profile_pic}
                    />
                  </div>
                  <div className="space-y-1">
                    <p>{data?.article?.authors?.[0]?.name}</p>
                    {data?.article?.authors?.[0]?.designation && (
                      <p className="text-sm">
                        {data?.article?.authors?.[0]?.designation}
                      </p>
                    )}
                  </div>
                </div>
                <p className="flex flex-col items-end text-sm uppercase md:flex-row md:space-x-1">
                  <span>PUBLISHED ON</span>
                  <span>{data?.article?.published_date}</span>
                </p>
              </div>
            </div>
            <div className="hidden lg:col-span-3"></div>
          </div>

          <div className="mt-12 grid w-full grid-cols-12">
            <div className="col-span-3 hidden lg:block"></div>
            <div className="col-span-12 lg:col-span-6">
              <div
                className="admin-text-editor"
                dangerouslySetInnerHTML={{ __html: data?.article?.description }}
              />
            </div>
            <div className="col-span-3 hidden lg:block"></div>
          </div>

          <div className="mt-14 block lg:hidden">
            <SocialShare
              shareAbleLink={BLOG_URL}
              title={data?.article?.title}
            />
          </div>

          {data?.related_articles?.length > 0 && (
            <>
              <div className="my-[3.75rem] lg:my-[6.25rem]">
                <div className="relative h-1 w-full">
                  <div className="absolute inset-0 border-t border-dashed border-gray-500"></div>
                </div>
              </div>

              <div>
                <h2 className="mb-[3.5rem] text-center text-2xl font-semibold lg:mb-[5rem] lg:text-5xl lg:font-medium">
                  More Blog Articles
                </h2>
                <BlogList
                  blogs={data?.related_articles?.slice(0, MAX_BLOG_TO_SHOW)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export const getStaticPaths = async () => {
  const { data } = await getBlogListData();

  const articles = data?.data?.articles;

  const paths = articles?.map(({ slug }) => ({ params: { slug } }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({ params }) => {
  const props = { data: null };

  const res = await getBlogData(params.slug);
  if (res?.status === statusCodes.OK) {
    props.data = res.data.data;
  }

  if (!props.data) {
    return { notFound: true };
  }

  return { props, revalidate: revalidationTime };
};

export default Blog;
